import { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../providers/ThemeContext";
import "./ServiceCard.css";

/**
 * Large service card for the Services page.
 *
 * fit="contain"  product cut-outs (transparent PNG / white background): shown whole,
 *                centered on a soft background with padding.
 * fit="cover"    real photos: fill the image area edge to edge.
 */

export interface ServiceCardProps {
  title: string;
  desc?: string;
  img: string;
  alt?: string;
  to: string;
  fit?: "contain" | "cover";
  ctaLabel?: string;
  /** use h2 when the cards are the main content of the page */
  headingTag?: "h2" | "h3";
}

export default function ServiceCard({
  title,
  desc,
  img,
  alt,
  to,
  fit = "cover",
  ctaLabel = "Learn more",
  headingTag: Heading = "h3",
}: ServiceCardProps) {
  const { theme } = useTheme();

  // colors come from the site theme, so light and dark mode both work
  const vars = {
    ["--sc-card" as string]: theme.card2 ?? theme.card ?? "#ffffff",
    ["--sc-text" as string]: theme.text ?? "#1d1d1f",
    ["--sc-muted" as string]: theme.text ?? "#5b5b66",
    ["--sc-border" as string]: theme.border ?? "rgba(0,0,0,0.08)",
    ["--sc-accent" as string]: theme.primary ?? "#c7645a",
  } as CSSProperties;

  return (
    <Link to={to} className={`sc sc--${fit}`} style={vars}>
      <div className="sc__media">
        <img src={img} alt={alt ?? title} loading="lazy" decoding="async" />
      </div>
      <div className="sc__body">
        <Heading className="sc__title">{title}</Heading>
        {desc && <p className="sc__desc">{desc}</p>}
        <span className="sc__cta">
          {ctaLabel}
          <span className="sc__arrow" aria-hidden="true">
            →
          </span>
        </span>
      </div>
    </Link>
  );
}
